// Editor del cuerpo de una comunicación.
//
// El cuerpo es una lista de bloques tipados (párrafo, lista, dato destacado, botón,
// separador), NO HTML: el servidor arma el HTML y escapa todo lo que se escribe acá.
// El párrafo se edita como texto plano con **negrita**, que se convierte a tramos.
import React, { useRef } from 'react';
import { Button, Dropdown, Input, Tooltip } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BoldOutlined,
  DeleteOutlined,
  LinkOutlined,
  MinusOutlined,
  OrderedListOutlined,
  PlusOutlined,
  StarOutlined,
  AlignLeftOutlined,
} from '@ant-design/icons';

const nuevoId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// ── Conversión editor ⇄ API ──────────────────────────────────────────────────

export const aFragmentos = (texto = '') =>
  texto
    .split(/(\*\*[^*]+\*\*)/g)
    .filter((t) => t !== '')
    .map((t) => (/^\*\*[^*]+\*\*$/.test(t) ? { texto: t.slice(2, -2), negrita: true } : { texto: t }));

export const aTexto = (fragmentos = []) => fragmentos.map((f) => (f.negrita ? `**${f.texto}**` : f.texto)).join('');

/** Bloques del editor → bloques del API (descarta los vacíos). */
export const aBloquesApi = (bloques) =>
  bloques
    .map((b) => {
      switch (b.tipo) {
        case 'parrafo':
          return b.texto?.trim() ? { tipo: 'parrafo', fragmentos: aFragmentos(b.texto.trim()) } : null;
        case 'lista': {
          const items = (b.texto || '').split('\n').map((l) => l.trim()).filter(Boolean);
          return items.length ? { tipo: 'lista', items: items.map(aFragmentos) } : null;
        }
        case 'destacado':
          return b.etiqueta?.trim() || b.valor?.trim() ? { tipo: 'destacado', etiqueta: b.etiqueta || '', valor: b.valor || '' } : null;
        case 'boton':
          return b.textoBoton?.trim() || b.url?.trim() ? { tipo: 'boton', texto: b.textoBoton || '', url: (b.url || '').trim() } : null;
        case 'separador':
          return { tipo: 'separador' };
        default:
          return null;
      }
    })
    .filter(Boolean);

/** Bloques del API → bloques del editor. */
export const deBloquesApi = (bloques = []) =>
  bloques.map((b) => {
    const base = { _id: nuevoId(), tipo: b.tipo };
    switch (b.tipo) {
      case 'parrafo': return { ...base, texto: aTexto(b.fragmentos) };
      case 'lista': return { ...base, texto: (b.items || []).map(aTexto).join('\n') };
      case 'destacado': return { ...base, etiqueta: b.etiqueta, valor: b.valor };
      case 'boton': return { ...base, textoBoton: b.texto, url: b.url };
      default: return base;
    }
  });

export const bloqueNuevo = (tipo) => ({ _id: nuevoId(), tipo, texto: '', etiqueta: '', valor: '', textoBoton: '', url: '' });

const TIPOS = [
  { key: 'parrafo', label: 'Párrafo', icon: <AlignLeftOutlined /> },
  { key: 'lista', label: 'Lista', icon: <OrderedListOutlined /> },
  { key: 'destacado', label: 'Dato destacado', icon: <StarOutlined /> },
  { key: 'boton', label: 'Botón con enlace', icon: <LinkOutlined /> },
  { key: 'separador', label: 'Separador', icon: <MinusOutlined /> },
];

function CampoParrafo({ bloque, onChange, placeholder, rows = 4 }) {
  const ref = useRef(null);
  // Envuelve la selección en **…** (o inserta **negrita** si no hay selección).
  const negrita = () => {
    const el = ref.current?.resizableTextArea?.textArea;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b, value } = el;
    const sel = value.slice(a, b) || 'texto';
    onChange({ texto: `${value.slice(0, a)}**${sel}**${value.slice(b)}` });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + 2, a + 2 + sel.length); });
  };
  return (
    <div>
      <div className="flex justify-end mb-1">
        <Tooltip title="Negrita (envuelve el texto seleccionado en **)">
          <Button size="small" type="text" icon={<BoldOutlined />} onClick={negrita} aria-label="Negrita" />
        </Tooltip>
      </div>
      <Input.TextArea ref={ref} rows={rows} value={bloque.texto} placeholder={placeholder} maxLength={2000}
        onChange={(e) => onChange({ texto: e.target.value })} autoSize={{ minRows: rows, maxRows: 14 }} />
    </div>
  );
}

function CuerpoBloque({ bloque, onChange }) {
  switch (bloque.tipo) {
    case 'parrafo':
      return <CampoParrafo bloque={bloque} onChange={onChange} placeholder="Escribe el texto. Usa **así** para negrita." />;
    case 'lista':
      return <CampoParrafo bloque={bloque} onChange={onChange} rows={3} placeholder={'Un punto por línea\nOtro punto'} />;
    case 'destacado':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input value={bloque.etiqueta} maxLength={60} placeholder="Etiqueta (ej. Nuevo horario)" onChange={(e) => onChange({ etiqueta: e.target.value })} />
          <Input value={bloque.valor} maxLength={120} placeholder="Valor (ej. Martes 19:00)" onChange={(e) => onChange({ valor: e.target.value })} />
        </div>
      );
    case 'boton':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2">
          <Input value={bloque.textoBoton} maxLength={40} placeholder="Texto del botón" onChange={(e) => onChange({ textoBoton: e.target.value })} />
          <Input value={bloque.url} maxLength={500} placeholder="https://…" onChange={(e) => onChange({ url: e.target.value })} />
        </div>
      );
    case 'separador':
      return <hr className="border-gray-200 my-2" />;
    default:
      return null;
  }
}

export default function EditorBloques({ bloques, onChange, limite = 30 }) {
  const hayBoton = bloques.some((b) => b.tipo === 'boton');
  const actualizar = (id, cambios) => onChange(bloques.map((b) => (b._id === id ? { ...b, ...cambios } : b)));
  const quitar = (id) => onChange(bloques.filter((b) => b._id !== id));
  const mover = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= bloques.length) return;
    const copia = [...bloques];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    onChange(copia);
  };
  const agregar = ({ key }) => onChange([...bloques, bloqueNuevo(key)]);

  const menu = {
    items: TIPOS.map((t) => ({
      key: t.key,
      label: t.label,
      icon: t.icon,
      disabled: t.key === 'boton' && hayBoton,
    })),
    onClick: agregar,
  };

  return (
    <div className="space-y-3">
      {bloques.map((b, i) => {
        const tipo = TIPOS.find((t) => t.key === b.tipo);
        return (
          <div key={b._id} className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                {tipo?.icon} {tipo?.label}
              </span>
              <div className="flex items-center">
                <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => mover(i, -1)} aria-label="Subir" />
                <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === bloques.length - 1} onClick={() => mover(i, 1)} aria-label="Bajar" />
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => quitar(b._id)} aria-label="Quitar bloque" />
              </div>
            </div>
            <CuerpoBloque bloque={b} onChange={(cambios) => actualizar(b._id, cambios)} />
          </div>
        );
      })}

      <div className="flex items-center justify-between">
        <Dropdown menu={menu} trigger={['click']} disabled={bloques.length >= limite}>
          <Button icon={<PlusOutlined />}>Agregar bloque</Button>
        </Dropdown>
        <span className="text-xs text-gray-400">{bloques.length} / {limite} bloques{hayBoton ? ' · ya hay un botón' : ''}</span>
      </div>
    </div>
  );
}
